import React, { FC, ReactNode, useState } from 'react';
import {
  formatToShortDate,
  formatToUniversalDateTime,
  formatToDateTime,
} from 'utils/dates';
import { formatPrice } from 'utils/functions';
import {
  GridLegacy,
  Typography,
  Tooltip,
  Stack,
  TypographyTypeMap,
  Box,
  SxProps,
  TooltipProps,
} from '@mui/material';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import CopyClipboard from 'ui-component/extended/CopyClipboard';
import { useTheme } from '@mui/material/styles';
import {
  RelatedRecord,
  StockLocation,
  StockLocationLite,
} from 'types/inventory';
import { User, UserLite } from 'types/user';
import { DateString, DateTimeString } from 'types';
import _ from 'lodash';
import UserInfoSection from 'ui-component/UserInfoSection';
import RelatedRecords from 'ui-component/shared/RelatedRecords';
import AddressCard from 'ui-component/cards/AddressCard';
import { IconPencil } from '@tabler/icons-react';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { PermissionScope } from 'types/apps';
import { StockLocationSchemaLite } from 'types/clientV2/stockLocations';
import { Tag } from 'types/clientV2/tag';
import Tags from 'ui-component/Tags';
import FacepileInfoSection from 'ui-component/FacepileInfoSection';
import FilepileInfoSection from 'ui-component/StockDocuments/FilepileInfoSection';
import { UserSchema } from 'types/clientV2/users';
import InlineLinkButton from 'ui-component/shared/InlineLinkButton';
import ReactQuill from 'react-quill';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import { StockDocumentRelatedObjectWithStockDocument } from 'types/clientV2/stockDocuments';
import InlineTrackingLink from 'ui-component/InlineTrackingLink';

export enum InfoSectionItemFormatTypes {
  string = 'string',
  number = 'number',
  dateShort = 'dateShort',
  dateMedium = 'dateMedium',
  dateLong = 'dateLong',
  priceShort = 'priceShort',
  price = 'price',
  children = 'children',
  stockLocationAddress = 'stockLocationAddress',
  user = 'user',
  users = 'users',
  relatedRecords = 'relatedRecords',
  boolean = 'boolean',
  tags = 'tags',
  documents = 'documents',
  url = 'url',
  richText = 'richText',
  trackingNumber = 'trackingNumber',
}

// TODO: update the typing for the functions --> is the value type correct also?
const formatFunctions: Record<string, Function> = {
  [InfoSectionItemFormatTypes.string]: (value: string) => value,
  [InfoSectionItemFormatTypes.richText]: (value: string) => value,
  [InfoSectionItemFormatTypes.url]: (value: string) => {
    if (value.includes('::')) {
      return value.split('::')[0];
    }
    return value;
  },
  [InfoSectionItemFormatTypes.number]: (value: number) =>
    _.toNumber(value) >= UNLIMITED_QUANTITY
      ? UNLIMITED_QUANTITY_STRING
      : value
      ? value.toLocaleString('en-US')
      : '-',
  [InfoSectionItemFormatTypes.dateShort]: (value: string) =>
    value ? formatToShortDate(value) : '-',
  [InfoSectionItemFormatTypes.dateMedium]: (value: string) =>
    value ? formatToDateTime(value) : '-',
  [InfoSectionItemFormatTypes.dateLong]: (value: string) =>
    value ? formatToUniversalDateTime(value) : '-',
  [InfoSectionItemFormatTypes.priceShort]: (value: string) =>
    formatPrice(parseFloat(value).toFixed(2)),
  [InfoSectionItemFormatTypes.price]: (value: string | number) =>
    formatPrice(value),
  [InfoSectionItemFormatTypes.children]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.stockLocationAddress]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.user]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.users]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.relatedRecords]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.boolean]: (value: boolean) =>
    value ? 'Yes' : 'No',
  [InfoSectionItemFormatTypes.tags]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.documents]: (value: boolean) => value,
  [InfoSectionItemFormatTypes.trackingNumber]: (value: string) => value,
};

export interface InfoSectionItemProps {
  caption?: string;
  isLoading?: boolean;
  value?:
    | string
    | number
    | boolean
    | undefined
    | null
    | Date
    | DateTimeString
    | DateString;
  userValue?: UserLite | User | UserSchema | null;
  usersValue?: (UserLite | User | UserSchema)[] | null;
  dateValue?: Date | string | null;
  relatedRecordsValue?: RelatedRecord[];
  stockLocationValue?:
    | StockLocation
    | StockLocationLite
    | StockLocationSchemaLite
    | null;
  tagsValue?: Tag[];
  formatType?: InfoSectionItemFormatTypes;
  error?: boolean;
  clipboard?: boolean;
  prefix?: string;
  suffix?: string;
  typographyProps?: {
    variant?: TypographyTypeMap['props']['variant'];
    sx?: TypographyTypeMap['props']['sx'];
  };
  sx?: SxProps;
  placeholder?: string;
  tooltip?: string;
  xs?: number | boolean;
  color?:
    | 'warning'
    | 'success'
    | 'error'
    | 'info'
    | 'primary'
    | 'secondary'
    | 'grey';
  editInline?: React.ReactElement;
  disableEdit?: boolean;
  permissionScope?: PermissionScope;
  onOpenEditRelatedRecords?: () => void;
  onAddRelatedRecordClick?: () => void;
  useInfoSectionAutomationOption?: boolean;
  documentsValue?: StockDocumentRelatedObjectWithStockDocument[];
  documentsOnClick?: (id: string | null) => void;
  onClick?: () => void;
  tooltipPlacement?: TooltipProps['placement'];
  backgroundColorAddressCard?: string;
  addressCardStartCollapsed?: boolean;
  children?: ReactNode;
}

const InfoSectionItem: FC<InfoSectionItemProps> = ({
  caption,
  isLoading = false,
  value,
  userValue,
  usersValue,
  dateValue,
  relatedRecordsValue,
  stockLocationValue,
  tagsValue,
  formatType = InfoSectionItemFormatTypes.string,
  children,
  error = false,
  clipboard = true,
  prefix = '',
  suffix = '',
  tooltip = '',
  typographyProps = {},
  sx = {},
  placeholder = '-',
  xs,
  color,
  editInline,
  disableEdit = false,
  permissionScope,
  onOpenEditRelatedRecords,
  onAddRelatedRecordClick,
  useInfoSectionAutomationOption = false,
  documentsValue,
  documentsOnClick,
  onClick,
  tooltipPlacement,
  backgroundColorAddressCard,
  addressCardStartCollapsed = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const theme = useTheme();

  const colorMap: { [key: string]: string } = {
    warning: theme.palette.warning.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    grey: theme.palette.grey[500],
  };

  let clonedEditInline = null;

  if (editInline) {
    clonedEditInline = React.cloneElement(editInline, {
      closeEditing: () => setIsEditing(false),
    });
  }

  // Check if value should be treated as truthy (including special case for priceShort)
  const hasValidValue =
    !!value ||
    (formatType === InfoSectionItemFormatTypes.priceShort &&
      value !== undefined &&
      value !== null) ||
    formatType === InfoSectionItemFormatTypes.boolean;

  const EditButton = editInline && !isEditing && (
    <SegmentIconButton
      color="primary"
      onClick={() => setIsEditing(true)}
      size="small"
      disabled={disableEdit}
      permissionScope={permissionScope}
    >
      <IconPencil stroke="1.5px" width={20} height={20} />
    </SegmentIconButton>
  );
  return (
    <Tooltip title={tooltip} placement={tooltipPlacement || undefined}>
      <GridLegacy item xs={xs || true} sx={sx}>
        {!!caption && <Typography variant="caption">{caption}</Typography>}
        {clonedEditInline && isEditing ? (
          <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
            {clonedEditInline}
          </Stack>
        ) : (
          <>
            <WithSkeleton
              isLoading={
                isLoading && formatType !== InfoSectionItemFormatTypes.documents
              }
              width={60}
            >
              {!_.includes(
                [
                  InfoSectionItemFormatTypes.children,
                  InfoSectionItemFormatTypes.stockLocationAddress,
                  InfoSectionItemFormatTypes.user,
                  InfoSectionItemFormatTypes.users,
                  InfoSectionItemFormatTypes.relatedRecords,
                  InfoSectionItemFormatTypes.tags,
                  InfoSectionItemFormatTypes.documents,
                  InfoSectionItemFormatTypes.richText,
                ],
                formatType
              ) && (
                <>
                  <Typography
                    variant={
                      typographyProps.variant ??
                      (value ||
                      formatType === InfoSectionItemFormatTypes.boolean ||
                      (formatType === InfoSectionItemFormatTypes.priceShort &&
                        value !== undefined &&
                        value !== null)
                        ? 'h4'
                        : 'body1')
                    }
                    sx={{
                      ...(error
                        ? { color: theme.palette.error.dark }
                        : color
                        ? { color: colorMap[color] }
                        : {}),
                      overflowWrap: 'anywhere',
                      fontStyle: hasValidValue ? 'normal' : 'italic',
                      ...typographyProps.sx,
                    }}
                    onClick={onClick}
                  >
                    {hasValidValue
                      ? `${prefix}${formatFunctions[formatType](
                          value
                        )}${suffix}`
                      : placeholder}
                    {EditButton}
                    {!!clipboard &&
                      formatType !== InfoSectionItemFormatTypes.url &&
                      hasValidValue && (
                        <CopyClipboard
                          label={caption || ''}
                          input={`${prefix}${formatFunctions[formatType](
                            value
                          )}${suffix}`}
                        />
                      )}
                    {!!value &&
                      formatType === InfoSectionItemFormatTypes.url && (
                        <InlineLinkButton
                          tooltip="View Link"
                          link={
                            (value as string).includes('::')
                              ? (value as string).split('::')[1]
                              : (value as string)
                          }
                        />
                      )}
                    {!!value &&
                      formatType ===
                        InfoSectionItemFormatTypes.trackingNumber && (
                        <InlineTrackingLink
                          trackingNumber={value as string | null | undefined}
                        />
                      )}
                    {children}
                  </Typography>
                </>
              )}
              {formatType === InfoSectionItemFormatTypes.children && (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Box sx={{ flex: 1 }}>{children}</Box>
                  {EditButton}
                </Stack>
              )}
              {formatType === InfoSectionItemFormatTypes.richText && (
                <GridLegacy
                  container
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <GridLegacy item xs>
                    <Box
                      sx={{
                        '& .ql-editor': {
                          padding: '0px !important',
                        },
                      }}
                    >
                      <ReactQuill
                        style={{
                          padding: '0px',
                          margin: '0px',
                        }}
                        value={value as string}
                        readOnly
                        theme="bubble"
                        modules={{ toolbar: false }}
                      />
                    </Box>
                  </GridLegacy>
                  <GridLegacy item xs="auto">
                    {EditButton}
                  </GridLegacy>
                </GridLegacy>
              )}
              {formatType === InfoSectionItemFormatTypes.user && (
                <>
                  <UserInfoSection
                    user={userValue || undefined}
                    subtitle={
                      dateValue
                        ? formatToDateTime(dateValue)
                        : userValue?.title || undefined
                    }
                    automation
                    showEmail
                  />
                  {EditButton}
                </>
              )}
              {formatType === InfoSectionItemFormatTypes.users &&
                (usersValue ? (
                  <>
                    <FacepileInfoSection users={usersValue} />
                    {EditButton}
                  </>
                ) : (
                  <Typography variant="h4">-{EditButton}</Typography>
                ))}
              {formatType === InfoSectionItemFormatTypes.documents && (
                <Stack>
                  <FilepileInfoSection
                    documents={documentsValue}
                    isLoading={isLoading}
                    onClick={documentsOnClick}
                  />
                  {EditButton}
                  {children}
                </Stack>
              )}
              {formatType === InfoSectionItemFormatTypes.relatedRecords && (
                <RelatedRecords
                  relatedRecords={relatedRecordsValue}
                  onOpenEditRelatedRecords={onOpenEditRelatedRecords}
                  EditButton={EditButton || undefined}
                  onAddRelatedRecordClick={onAddRelatedRecordClick}
                />
              )}
              {formatType === InfoSectionItemFormatTypes.tags && (
                <>
                  <Tags tags={tagsValue} />
                  {EditButton}
                </>
              )}
              {formatType === InfoSectionItemFormatTypes.stockLocationAddress &&
                (stockLocationValue ? (
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <AddressCard
                      location={stockLocationValue}
                      backgroundColor={backgroundColorAddressCard}
                      startCollapsed={addressCardStartCollapsed}
                    />
                    <Stack
                      direction="column"
                      spacing={0}
                      sx={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      {EditButton}
                      {children}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      ...(error
                        ? { color: theme.palette.error.dark }
                        : color
                        ? { color: colorMap[color] }
                        : {}),
                      fontStyle: 'italic',
                    }}
                  >
                    {placeholder || '-'}
                    <Stack
                      direction="column"
                      spacing={0}
                      sx={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      {EditButton}
                      {children}
                    </Stack>
                  </Typography>
                ))}
            </WithSkeleton>
          </>
        )}
      </GridLegacy>
    </Tooltip>
  );
};

export default InfoSectionItem;
